import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

//Common
import { Drawer, LoadingIndicator, Snackbar } from "../common/VFX-UI";

//App
import { queryKeys } from "./constants";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../utility/constants/constants";
import { selectIsActive, selectSearchValue } from "../../store/organization";
import {
  OrganizationContextData,
  OrganizationUseState,
} from "../../types/organization";
import organizationApi from "../../app/model/api/organizationApi";
import OrganizationView from "./OrganizationView";
import { useOrganization } from "./hooks/useOrganization";
import { OrganizationFormContainer } from "./OrganizationFormContainer";
import { useNavigate } from "react-router-dom";

export const OrganizationViewContext =
  React.createContext<OrganizationContextData | null>(null);

const OrganizationContainer = () => {
  const organizationId = window.location.pathname.split("/")[2];

  const {
    state,
    toggleEditViewHandler,
    toggleActivateHandler,
    toggleDeactivateHandler,
    onAddSnackbarClose,
    onShowSuccess,
  } = useOrganization({
    deleteMutation: function (): void {},
  });

  const [stateOrg, setStateOrg] = useState<OrganizationUseState>({
    stateId: null,
    organizationId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    currentPage: 1,
    pageSize: GRID_PAGE_SIZE,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const navigate = useNavigate();
  const toggleHandlerView = () => {
    navigate(-1);
  };
  const organizationGridQuery = useQuery({
    queryKey: [queryKeys.getOrganizations],
    queryFn: () =>
      organizationApi.searchOrganizations({
        ...stateOrg.searchPayload,
        isActive: stateOrg.activeToggle,
        pageSize: stateOrg.pageSize,
        pageNumber: stateOrg.currentPage,
        sortBy: stateOrg.sortBy,
      }),
  });

  const updateOrganizationId = async (organizationId: string | null) => {
    setStateOrg((prevState) => ({
      ...prevState,
      organizationId: organizationId,
    }));
  };

  const updateStateId = async (stateId: string | null) => {
    setStateOrg((prevState) => ({
      ...prevState,
      searchPayload: null,
      stateId: stateId,
    }));
  };

  const getOrganizationQuery = useQuery({
    queryKey: [queryKeys.getOrganization, organizationId],
    queryFn: () => organizationApi.get(organizationId ?? EMPTY_GUID),
    enabled: organizationId !== null,
  });

  const contextValue: OrganizationContextData = {
    refetchOrganization: () => getOrganizationQuery.refetch(),
    data: getOrganizationQuery?.data?.result,
    id: organizationId,
    stateId: stateOrg.stateId,
    setStateId: updateStateId,
    setId: updateOrganizationId,
    refetchGrid: () => organizationGridQuery.refetch(),
    pageSize: stateOrg.pageSize,
  };

  state.showSuccessSnackbar && contextValue.refetchOrganization();

  return organizationGridQuery.isLoading ? (
    <LoadingIndicator />
  ) : (
    <OrganizationViewContext.Provider value={contextValue}>
      <OrganizationView
        onClose={toggleHandlerView}
        editAction={() => {
          toggleEditViewHandler();
        }}
        toggleDeactivateHandler={toggleDeactivateHandler}
        toggleActivateHandler={toggleActivateHandler}
      />
      <Drawer show={state.toggleEditForm}>
        {state.toggleEditForm && contextValue?.data && (
          <OrganizationFormContainer
            onClose={toggleEditViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>
      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}
    </OrganizationViewContext.Provider>
  );
};

export default OrganizationContainer;
